if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();
        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.requestId = 0;
        this.requestTimeout = null;
        this.abortController = null;
        this.drawer = null;
      }

      connectedCallback() {
        if (this.hasAttribute('available')) this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.cancelAvailabilityRequest();
        this.removeDrawer();
      }

      cancelAvailabilityRequest() {
        this.requestId += 1;
        this.abortController?.abort();
        this.abortController = null;
        clearTimeout(this.requestTimeout);
        this.requestTimeout = null;
      }

      isCurrentAvailabilityRequest(requestId, variantId) {
        return this.isConnected && this.requestId === requestId && this.dataset.variantId === String(variantId);
      }

      fetchAvailability(variantId) {
        this.cancelAvailabilityRequest();
        if (!variantId || !this.isConnected) return;

        this.dataset.variantId = String(variantId);
        const requestId = this.requestId;
        const controller = new AbortController();
        this.abortController = controller;
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) rootUrl += '/';
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        this.requestTimeout = setTimeout(() => {
          if (!this.isCurrentAvailabilityRequest(requestId, variantId)) return;
          this.cancelAvailabilityRequest();
          this.renderError();
        }, 15000);

        return fetch(variantSectionUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Pickup availability request failed');
            return response.text();
          })
          .then((text) => {
            if (!this.isCurrentAvailabilityRequest(requestId, variantId)) return;
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            if (!sectionInnerHTML) throw new Error('Pickup availability section missing');
            this.renderPreview(sectionInnerHTML);
          })
          .catch(() => {
            if (this.isCurrentAvailabilityRequest(requestId, variantId)) this.renderError();
          })
          .finally(() => {
            if (this.requestId !== requestId) return;
            clearTimeout(this.requestTimeout);
            this.requestTimeout = null;
            this.abortController = null;
          });
      }

      onClickRefreshList() {
        return this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        this.dataset.variantId = variant?.id ? String(variant.id) : '';
        if (variant?.available) return this.fetchAvailability(variant.id);
        this.cancelAvailabilityRequest();
        this.removeAttribute('available');
        this.replaceChildren();
        this.removeDrawer();
      }

      renderError() {
        this.removeDrawer();
        this.replaceChildren();
        if (!this.errorHtml) {
          this.removeAttribute('available');
          return;
        }
        this.appendChild(this.errorHtml.cloneNode(true));
        this.querySelector('button')?.addEventListener('click', this.onClickRefreshList);
      }

      removeDrawer() {
        if (this.drawer?.isConnected && this.drawer.hasAttribute('open')) this.drawer.hide();
        this.drawer?.remove();
        this.drawer = null;
      }

      renderPreview(sectionInnerHTML) {
        const previousDrawer = document.querySelector('pickup-availability-drawer');
        if (previousDrawer?.hasAttribute('open')) previousDrawer.hide();
        previousDrawer?.remove();
        this.drawer = null;
        const preview = sectionInnerHTML.querySelector('pickup-availability-preview');
        if (!preview) {
          this.replaceChildren();
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');
        this.drawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
        if (!this.drawer) return;
        document.body.appendChild(this.drawer);
        (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean).forEach((colorClass) => {
          this.drawer.classList.add(colorClass);
        });
        this.querySelector('button')?.addEventListener('click', (event) => {
          if (this.drawer?.isConnected) this.drawer.show(event.currentTarget);
        });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      disconnectedCallback() {
        document.body.removeEventListener('click', this.onBodyClick);
        if (this.hasAttribute('open')) this.hide();
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}

